import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { WifiOff, Refresh } from '@mui/icons-material';
import { checkFirebaseConnectivity } from '../utils/networkCheck';
import { useNavigate } from 'react-router-dom';

interface NetworkErrorDialogProps {
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
  errorType?: 'auth' | 'database' | 'storage' | 'general';
  errorMessage?: string;
}

/**
 * A dialog component that provides useful information and actions when network errors occur
 */
const NetworkErrorDialog: React.FC<NetworkErrorDialogProps> = ({
  open,
  onClose,
  onRetry,
  errorType = 'general',
  errorMessage
}) => {
  const [checking, setChecking] = useState(false);
  const [connectivityStatus, setConnectivityStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');
  const navigate = useNavigate();

  // Check Firebase connectivity when dialog opens
  useEffect(() => {
    if (open) {
      checkConnectivity();
    }
  }, [open]);

  // Function to check connectivity
  const checkConnectivity = async () => {
    setChecking(true);
    try {
      const isConnected = await checkFirebaseConnectivity();
      setConnectivityStatus(isConnected ? 'connected' : 'disconnected');
    } catch (error) {
      setConnectivityStatus('disconnected');
    } finally {
      setChecking(false);
    }
  };

  // Handle retry action
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
    onClose();
  };

  // Get error title based on type
  const getErrorTitle = () => {
    switch (errorType) {
      case 'auth':
        return 'Authentication Connection Error';
      case 'database':
        return 'Database Connection Error';
      case 'storage':
        return 'Storage Connection Error';
      default:
        return 'Network Connection Error';
    }
  };

  // Get error message based on type and connectivity status
  const getErrorDescription = () => {
    if (errorMessage) {
      return errorMessage;
    }

    if (connectivityStatus === 'disconnected') {
      return 'Unable to connect to Firebase services. Please check your internet connection and try again.';
    }

    switch (errorType) {
      case 'auth':
        return 'Unable to authenticate with the server. This could be due to network issues or service interruption.';
      case 'database':
        return 'Unable to connect to the database. Your changes may not be saved until connection is restored.';
      case 'storage':
        return 'Unable to access storage services. File uploads and downloads may fail until connection is restored.';
      default:
        return 'A network error has occurred. Please check your connection and try again.';
    }
  };

  // Get troubleshooting steps
  const getTroubleshootingSteps = () => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Troubleshooting steps:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
          <li>Check your internet connection</li>
          <li>Disable VPN or proxy if you're using one</li>
          <li>Clear your browser cache and cookies</li>
          <li>Try using a different network</li>
          <li>Restart your browser</li>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="network-error-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="network-error-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <WifiOff color="error" sx={{ mr: 1 }} />
          {getErrorTitle()}
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {getErrorDescription()}
        </DialogContentText>

        {getTroubleshootingSteps()}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Firebase connection status:
          </Typography>
          {checking ? (
            <CircularProgress size={16} thickness={4} />
          ) : (
            <Typography
              variant="body2"
              color={connectivityStatus === 'connected' ? 'success.main' : 'error.main'}
            >
              {connectivityStatus === 'connected'
                ? 'Connected'
                : connectivityStatus === 'disconnected'
                ? 'Disconnected'
                : 'Unknown'}
            </Typography>
          )}
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={checkConnectivity}
            sx={{ ml: 1 }}
            disabled={checking}
          >
            Check Again
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            onClose();
            navigate('/firebase-troubleshooting');
          }}
          color="info"
        >
          Troubleshoot
        </Button>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleRetry}
          variant="contained"
          startIcon={<Refresh />}
          disabled={checking}
        >
          Retry
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NetworkErrorDialog; 